import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import Preview from '@renderer/components/Preview'
import { flashEffect } from '@renderer/utils/flashUtils'
import { useConfigStore } from '@renderer/stores/configState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { backgroundFilterProcessor } from '@renderer/utils/backgroundFilterProcessor'
import { Recorder } from 'canvas-record'
import { AVC } from 'media-codecs'
import cogoToast from '@successtar/cogo-toast'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'

const JapaneseCapture: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { setSessionState, userPreferences, id, uploadSessionState, logActivity } =
    useSessionStateStore()
  const { getFilter, filters, getFrame, frames } = useConfigStore()
  const { downloadLocal, cameraSettings } = useSettingsStore()

  // Helper function to normalize captureRatio - 'auto' means no cropping
  const getNormalizedCaptureRatio = (): number | undefined => {
    if (cameraSettings.captureRatio === 'auto') return undefined
    if (typeof cameraSettings.captureRatio === 'number') return cameraSettings.captureRatio
    return 4 / 3 // Default fallback
  }

  // Get photo count from selected frame - Use actual photo count from frame
  const selectedFrame = getFrame(userPreferences.frameId || frames[0]?._id || '')
  const uniquePhotoTypes = new Set(
    selectedFrame?.drawParams.images
      .map((image) => image.type)
      .filter((type) => type.startsWith('photo-')) || []
  )
  const photoCount = uniquePhotoTypes.size || 1 // Use actual count, fallback to 1

  // States
  const [capturedPhotoUrl, setCapturedPhotoUrl] = useState<string | null>(null)
  const [countdown, setCountdown] = useState<number>(5)
  const [isCapturing, setIsCapturing] = useState(false)
  const [preparing, setPreparing] = useState(true)
  const [remainingTime, setRemainingTime] = useState<string>('10:00')
  const [sourceRatio, setSourceRatio] = useState<number | null>(null)
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState<number>(0)
  const isRetakeMode = useRef(false)

  // Recording states
  const FPS = 30
  const [canvasDimensions, setCanvasDimensions] = useState<{
    width: number
    height: number
  } | null>(null)
  const captureCanvasRecorderRef = useRef<Recorder | null>(null)

  const stopRef = useRef<boolean>(false)
  const selectedFilter = getFilter(userPreferences.filterId || filters[0]._id)

  // Countdown hooks
  const prepCountdownHook = useCountdown(3)
  const captureCountdownHook = useCountdown(5, { audioCue: true })
  const timeoutCountdown = useCountdown(300) // 5 minutes

  useEffect(() => {
    uploadSessionState()
    initializeCapture()

    return (): void => {
      stopRef.current = true
      prepCountdownHook.stop()
      captureCountdownHook.stop()
      timeoutCountdown.stop()
    }
  }, [])

  // Attach recorder to canvas when ready
  useEffect((): (() => void) => {
    const canvas = document.querySelector('canvas')
    const canvasContext = canvas?.getContext('bitmaprenderer')
    if (canvasContext && canvas && canvasDimensions) {
      const newCaptureRecorder = new Recorder(canvasContext as RenderingContext, {
        name: `dreambooth-japanese-capture`,
        encoderOptions: {
          codec: AVC.getCodec({ profile: 'Baseline', level: '4.1' }),
          bitrate: 2000000
        },
        duration: Infinity,
        download: true,
        frameRate: FPS
      })
      captureCanvasRecorderRef.current = newCaptureRecorder
    }
    return () => {
      captureCanvasRecorderRef.current?.stop()
    }
  }, [canvasDimensions])

  const initializeCapture = (): void => {
    logActivity('Japanese capture session started', {
      photoCount,
      frameId: userPreferences.frameId
    })

    // Set up timeout countdown
    timeoutCountdown.setOnTick(() => {
      setRemainingTime(timeoutCountdown.getFormattedRemainingSeconds())
    })

    timeoutCountdown.setOnComplete(() => {
      logActivity('Japanese capture session timed out')
      handleNext()
    })

    timeoutCountdown.start()

    // Set up prep countdown
    prepCountdownHook.setOnComplete(() => {
      setPreparing(false)
      startCapture()
    })

    prepCountdownHook.start()
  }

  const startCapture = (): void => {
    setIsCapturing(true)
    setCountdown(5)

    // Set up capture countdown
    captureCountdownHook.setRemainingSeconds(5)
    captureCountdownHook.setOnTick(() => {
      setCountdown(captureCountdownHook.getRemainingSeconds())
    })

    captureCountdownHook.setOnComplete(() => {
      handleCapture()
    })

    // Autofocus now, while the countdown runs, so the shutter is not waiting on it (camera_prefocus).
    unifiedCameraUtils.prefocus()
    captureCountdownHook.start()
  }

  const handleCapture = async (): Promise<void> => {
    try {
      logActivity(`Capturing Japanese ID-style photo ${currentPhotoIndex + 1}/${photoCount}`)

      setSessionState({
        // Stats tracked by Gallery API
      })

      flashEffect('.flash')
      setIsCapturing(false)

      await new Promise((resolve) => setTimeout(resolve, 200))

      const imageData = await unifiedCameraUtils.captureImage(
        userPreferences.selectedCamera as 'primary' | 'secondary',
        undefined,
        getNormalizedCaptureRatio(),
        !cameraSettings.useShutterCapture
      )

      if (imageData) {
        const imageUrl = URL.createObjectURL(imageData)
        const captureId = `${id}-japanese-${currentPhotoIndex + 1}-${Date.now()}`

        if (downloadLocal) {
          const a = document.createElement('a')
          a.href = imageUrl
          a.download = `dreambooth-${id}-japanese-${currentPhotoIndex + 1}-${Date.now()}.jpg`
          a.setAttribute('data-automated-click', 'true')
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)

          logActivity(`Downloaded Japanese photo ${currentPhotoIndex + 1}`, {
            type: 'photo',
            filename: `dreambooth-${id}-japanese-${currentPhotoIndex + 1}-${Date.now()}.jpg`
          })
        }

        setCapturedPhotoUrl(imageUrl)

        // Update session state
        const sessionItems = useSessionStateStore.getState().gallery.capturedItems || []
        const capturedItem = {
          id: captureId,
          photoUrl: imageUrl,
          timestamp: new Date()
        }

        console.log('isRetakeMode', isRetakeMode.current)

        if (isRetakeMode.current) {
          // Replace the photo at current index
          const updatedItems = [...sessionItems]
          updatedItems[currentPhotoIndex] = capturedItem

          setSessionState({
            gallery: {
              capturedItems: updatedItems,
              filterCache: {} // Clear all filter caches on retake to prevent stale data
            }
          })

          isRetakeMode.current = false // Exit retake mode
        } else {
          // Add new photo to the end
          setSessionState({
            gallery: {
              capturedItems: [...sessionItems, capturedItem]
            }
          })
        }

        // Start background filter processing for this photo
        backgroundFilterProcessor.processAllFiltersForPhoto(imageUrl, capturedItem.id)

        logActivity(`Japanese photo ${currentPhotoIndex + 1} captured successfully`)
      } else {
        cogoToast.error('Failed to capture photo')
        setIsCapturing(false)
      }
    } catch (error) {
      console.error('Capture failed:', error)
      cogoToast.error('Capture failed')
      setIsCapturing(false)
    }
  }

  const handleRetake = (): void => {
    logActivity(`Starting retake for Japanese photo ${currentPhotoIndex + 1}`)
    isRetakeMode.current = true

    setCapturedPhotoUrl(null)
    startCapture()
  }

  const handleNext = (): void => {
    if (currentPhotoIndex + 1 < photoCount) {
      // Move to next photo
      setCurrentPhotoIndex(currentPhotoIndex + 1)
      setCapturedPhotoUrl(null)
      isRetakeMode.current = false
      startCapture()
      logActivity(`Moving to next Japanese photo: ${currentPhotoIndex + 2}/${photoCount}`)
    } else {
      // All photos complete, proceed to filter
      logActivity('Japanese capture completed, proceeding to filter step')
      uploadSessionState()
      navigate(`/${projectId}/filter`)
    }
  }

  // Calculate image style based on source ratio
  const getImageStyle = (): React.CSSProperties => {
    if (!sourceRatio) return { visibility: 'hidden' as const }

    return {
      width: '100%',
      height: '100%',
      objectFit: 'cover' as const,
      position: 'absolute' as const,
      top: '0',
      left: '0'
    }
  }

  // Load image and get its ratio when photo is captured
  useEffect(() => {
    if (capturedPhotoUrl) {
      const img = new Image()
      img.onload = (): void => {
        setSourceRatio(img.width / img.height)
      }
      img.src = capturedPhotoUrl
    }
  }, [capturedPhotoUrl])

  // Call all hooks at the top level
  const cachedJapaneseBg = useCachedBackgroundImage('/assets/boothndaughter/take-japanese.webp')
  const cachedRetakeBg = useCachedBackgroundImage('/assets/boothndaughter/retake.webp')
  const cachedNextBg = useCachedBackgroundImage('/assets/boothndaughter/next.webp')

  return (
    <div
      className="w-full h-full relative overflow-hidden"
      style={{
        backgroundImage: `url(${cachedJapaneseBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <div id="flash" className="w-full h-full flash"></div>

      {/* Countdown Display */}
      {isCapturing && (
        <div
          className="absolute"
          style={{
            top: '500px',
            right: '222px',
            fontFamily: 'monospace',
            fontSize: '160px',
            color: 'white',
            textShadow: '4px 4px 8px rgba(0,0,0,0.8)',
            fontWeight: 'bold'
          }}
        >
          {countdown}
        </div>
      )}

      {/* Preview Area - Always show on right */}
      <div
        className="absolute left-1/2 transform -translate-x-1/2"
        style={{
          top: '271px',
          width: '619px',
          height: '725px'
        }}
      >
        <Preview
          key={`${selectedFilter?._id}-${cameraSettings.mirror}-${cameraSettings.previewRotation}-${cameraSettings.captureRatio}`}
          adjustments={selectedFilter?.adjustments}
          ratio={619 / 725} // ID photo ratio
          cameraKey={userPreferences.selectedCamera as 'primary' | 'secondary'}
          filter={selectedFilter?._id}
          onCanvasReady={setCanvasDimensions}
        />
      </div>

      {/* Captured Photo Display - Smaller on left side */}
      {capturedPhotoUrl && (
        <div
          className="absolute"
          style={{
            left: '130px',
            top: '300px',
            width: '300px',
            height: '400px'
          }}
        >
          {/* Bouncy Retake Prompt - Above captured photo */}
          <div
            className="absolute -top-20 left-16 animate-bounce"
            style={{
              width: '200px',
              height: '60px'
            }}
          >
            <img
              src="/assets/boothndaughter/retake-prompt.webp"
              alt="Retake Prompt"
              className="w-full h-full object-contain"
            />
          </div>

          <div
            className="relative w-full h-full border-2 border-white rounded-lg overflow-hidden shadow-xl"
            style={{
              aspectRatio: '619/725'
            }}
          >
            <img
              src={capturedPhotoUrl}
              alt="Captured Photo"
              style={getImageStyle()}
              crossOrigin="anonymous"
            />
            {/* Retaking... overlay */}
            {isCapturing && isRetakeMode && (
              <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center z-10">
                <span
                  style={{
                    color: 'white',
                    fontSize: '2.5rem',
                    fontWeight: 'bold',
                    textShadow: '2px 2px 8px rgba(0,0,0,0.7)',
                    letterSpacing: '2px'
                  }}
                >
                  Retaking...
                </span>
              </div>
            )}
          </div>

          {/* Photo Progress */}
          <div className="text-center mt-2">
            <span
              style={{
                fontFamily: 'monospace',
                fontSize: '18px',
                color: 'white',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
              }}
            >
              Photo {currentPhotoIndex + 1} / {photoCount}
            </span>
          </div>

          {/* Retake Button below captured photo */}
          <div className="flex justify-center mt-4">
            <button
              onClick={handleRetake}
              title="Retake photo"
              style={{
                width: '160px',
                height: '72px',
                backgroundImage: `url(${cachedRetakeBg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                border: 'none',
                cursor: isCapturing ? 'not-allowed' : 'pointer',
                opacity: isCapturing ? 0.5 : 1
              }}
              className="hover:scale-105 transition-transform"
              disabled={isCapturing}
            />
          </div>
        </div>
      )}

      {/* Next Button - Right side when capture is complete */}
      {capturedPhotoUrl && (
        <div
          className="absolute right-32"
          style={{
            bottom: '100px',
            right: '100px'
          }}
        >
          <button
            onClick={handleNext}
            title={
              currentPhotoIndex + 1 < photoCount
                ? 'Continue to next photo'
                : 'Continue to next step'
            }
            style={{
              width: '150px',
              height: '90px',
              backgroundImage: `url(${cachedNextBg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              border: 'none',
              cursor: 'pointer'
            }}
            className="hover:scale-105 transition-transform"
          />
        </div>
      )}

      {/* Timeout Display */}
      <div className="absolute top-8 right-8">
        <div className="w-20 h-20 border-4 border-red-500 rounded-full flex items-center justify-center">
          <span
            style={{
              fontFamily: 'monospace',
              fontSize: '20px',
              color: '#EF4444',
              fontWeight: 'bold'
            }}
          >
            {remainingTime}
          </span>
        </div>
      </div>

      {/* Get Ready Overlay */}
      {preparing && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="text-center">
            <h1
              style={{
                fontFamily: 'monospace',
                fontSize: '64px',
                color: 'white',
                textShadow: '4px 4px 8px rgba(0,0,0,0.8)',
                fontWeight: 'bold',
                marginBottom: '24px'
              }}
            >
              Get Ready!
            </h1>
            <p
              style={{
                fontFamily: 'monospace',
                fontSize: '24px',
                color: 'white',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
              }}
            >
              {photoCount} ID-style photo{photoCount > 1 ? 's will' : ' will'} be taken
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default JapaneseCapture
